import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, ShieldAlert } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

const LISTS = [["opening_approaches", "Opening approaches"], ["talking_points", "Talking points"], ["next_actions", "Next actions"],
  ["success_criteria", "Success criteria"], ["do_not_say", "Never say"]];
const PAIRS = [["likely_questions", "Likely questions", "question", "answer"], ["objections", "Objections", "objection", "response"]];
const CLS = { service: "bg-emerald-50 text-emerald-800", promotional: "bg-rose-50 text-rose-800", unclear: "bg-amber-50 text-amber-900" };

function Generate({ cid, onDone }) {
  const [tp, setTp] = useState(""), [langs, setLangs] = useState(["en", "hi"]), [busy, setBusy] = useState(false);
  const go = () => {
    setBusy(true);
    api.post(`/outbound/campaigns/${cid}/strategy/generate`, { talking_points: tp, languages: langs })
      .then(() => { toast.success("Strategy drafted. Review it below."); onDone(); }).catch((e) => toast.error(apiErr(e))).finally(() => setBusy(false));
  };
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3" data-testid="ob-strat-generate">
      <label className="block text-xs">Talking points (facts, what to mention, what to avoid)
        <textarea rows={3} value={tp} onChange={(e) => setTp(e.target.value)} className="mt-1 block w-full rounded border px-2 py-1" data-testid="ob-strat-tp" /></label>
      <div className="flex flex-wrap items-center gap-3 text-xs">Languages:
        {[["en", "English"], ["hi", "Hindi"]].map(([k, l]) => <label key={k} className="flex items-center gap-1"><input type="checkbox" checked={langs.includes(k)}
          onChange={(e) => setLangs(e.target.checked ? [...langs, k] : langs.filter((x) => x !== k))} data-testid={`ob-strat-lang-${k}`} />{l}</label>)}
        <Button size="sm" onClick={go} disabled={busy || !langs.length} data-testid="ob-strat-generate-btn"><Sparkles className="mr-1 h-4 w-4" />{busy ? "Thinking… (up to a minute)" : "Generate strategy"}</Button>
      </div>
    </div>
  );
}

const toLines = (a) => (a || []).join("\n");
const toPairs = (a, x, y) => (a || []).map((p) => `${p[x]} || ${p[y]}`).join("\n");

function Editor({ cid, s, onDone }) {
  const c = s.content;
  const [v, set] = useState(() => ({ goal: c.goal, language_guidance: c.language_guidance,
    ...Object.fromEntries(LISTS.map(([k]) => [k, toLines(c[k])])), ...Object.fromEntries(PAIRS.map(([k, , x, y]) => [k, toPairs(c[k], x, y)])) }));
  const save = () => {
    const lines = (t) => t.split("\n").map((l) => l.trim()).filter(Boolean);
    const content = { goal: v.goal, language_guidance: v.language_guidance, ...Object.fromEntries(LISTS.map(([k]) => [k, lines(v[k])])),
      ...Object.fromEntries(PAIRS.map(([k, , x, y]) => [k, lines(v[k]).map((l) => { const [a, ...b] = l.split("||"); return { [x]: a.trim(), [y]: b.join("||").trim() }; })])) };
    api.put(`/outbound/campaigns/${cid}/strategy/${s.id}`, { content }).then(() => { toast.success("Edits saved"); onDone(); }).catch((e) => toast.error(apiErr(e)));
  };
  const area = (k, label, hint) => (
    <label key={k} className="block text-xs">{label}{hint && <span className="text-slate-400"> · {hint}</span>}
      <textarea rows={3} value={v[k]} onChange={(e) => set({ ...v, [k]: e.target.value })} className="mt-1 block w-full rounded border px-2 py-1" data-testid={`ob-strat-edit-${k}`} /></label>);
  return (
    <div className="space-y-2" data-testid="ob-strat-editor">
      {area("goal", "Goal")}
      {LISTS.map(([k, l]) => area(k, l, "one per line"))}
      {PAIRS.map(([k, l]) => area(k, l, "one per line: question || answer"))}
      {area("language_guidance", "Language guidance")}
      <Button size="sm" variant="outline" onClick={save} data-testid="ob-strat-save">Save edits</Button>
    </div>
  );
}

function View({ c }) {
  return (
    <div className="space-y-2 text-xs" data-testid="ob-strat-view">
      <p><b>Goal:</b> {c.goal}</p>
      {LISTS.map(([k, l]) => c[k]?.length > 0 && <div key={k}><b>{l}</b><ul className="list-disc pl-5">{c[k].map((x, i) => <li key={i}>{x}</li>)}</ul></div>)}
      {PAIRS.map(([k, l, x, y]) => c[k]?.length > 0 && <div key={k}><b>{l}</b><ul className="list-disc pl-5">{c[k].map((p, i) => <li key={i}><i>{p[x]}</i> → {p[y]}</li>)}</ul></div>)}
      <p><b>Language:</b> {c.language_guidance}</p>
    </div>
  );
}

function Version({ cid, s, editable, onDone }) {
  const [edit, setEdit] = useState(false), [ok, setOk] = useState(false);
  const cls = s.content.compliance;
  const approve = () => api.post(`/outbound/campaigns/${cid}/strategy/${s.id}/approve`, { confirm: true })
    .then(() => { toast.success("Strategy approved — campaign is Ready"); onDone(); }).catch((e) => toast.error(apiErr(e)));
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3" data-testid={`ob-strat-v${s.version}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <b>Version {s.version}</b><span className="rounded bg-slate-100 px-2 py-0.5" data-testid={`ob-strat-status-${s.version}`}>{s.status}</span>
        <span className={`rounded px-2 py-0.5 ${CLS[cls.classification]}`} data-testid={`ob-strat-class-${s.version}`}>Compliance: {cls.classification}</span>
        <span className="text-slate-500">{cls.reason}</span>
      </div>
      {edit ? <Editor cid={cid} s={s} onDone={() => { setEdit(false); onDone(); }} /> : <View c={s.content} />}
      {editable && s.status === "draft" && (
        <div className="flex flex-wrap items-center gap-2 border-t pt-2">
          <Button size="sm" variant="outline" onClick={() => setEdit(!edit)} data-testid={`ob-strat-edit-btn-${s.version}`}>{edit ? "Close editor" : "Edit"}</Button>
          {cls.classification === "service" ? <>
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid={`ob-strat-ack-${s.version}`} />I reviewed this strategy; every fact is correct.</label>
            <Button size="sm" disabled={!ok} onClick={approve} data-testid={`ob-strat-approve-${s.version}`}><CheckCircle2 className="mr-1 h-4 w-4" />Approve</Button></>
            : <p className="flex items-center gap-1 text-xs text-rose-700"><ShieldAlert className="h-4 w-4" />Needs separate compliance authorization: remove offers or sales content and regenerate.</p>}
        </div>)}
    </div>
  );
}

export function StrategyPanel({ camp }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["ob", "strat", camp.id], queryFn: async () => (await api.get(`/outbound/campaigns/${camp.id}/strategy`)).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["ob"] });
  const editable = ["draft", "awaiting_approval", "ready"].includes(camp.status);
  return (
    <div className="space-y-3" data-testid="ob-strategy">
      <p className="text-xs text-slate-600">Shally drafts a flexible plan from your objective, talking points and approved business knowledge. It is guidance, not a word-for-word script. You approve it before the campaign can run.</p>
      {editable && <Generate cid={camp.id} onDone={refresh} />}
      {(q.data || []).map((s) => <Version key={s.id} cid={camp.id} s={s} editable={editable} onDone={refresh} />)}
      {q.data && !q.data.length && <p className="text-xs text-slate-500" data-testid="ob-strat-empty">No strategy yet.</p>}
    </div>
  );
}
